import { createBuilder } from '@rocicorp/zero';
import { PUBLIC_ORGANIZATION_ID } from '../ent/root-entity-ids';
import { schema } from '../zero-schema';

/**
 * Context threaded through every custom query, client and server. `sub` is
 * the Clerk user id -- server-side it comes from a verified session (see
 * zero-request-auth.ts), client-side from ZeroClientProvider's `context`
 * option, matching what `definePermissions`' `authData.sub` used to be.
 */
export type QueryCtx = { sub: string };

/** Shared query-builder instance -- the same API `definePermissions` used. */
export const zb = createBuilder(schema);

// Predicates below are ported verbatim from zero-schema.ts's definePermissions
// callback: `authData.sub` -> `ctx.sub`. That callback still exists (legacy
// queries are enabled), so the two predicate sets must be kept in sync.
//
// zero-cache never applies definePermissions to synced queries (it only
// transforms non-custom ASTs, view-syncer.js in @rocicorp/zero 1.9), and that
// includes their `.related()` and `exists()` subqueries. Every subquery that
// reaches a protected table needs that table's predicate here, not just the
// root.

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const callerMembership = (ctx: QueryCtx, ouq: any) =>
  ouq.whereExists('user', (uq: any) => uq.where('clerkId', '=', ctx.sub));

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isOrgMember = (ctx: QueryCtx, eb: any) =>
  eb.exists('organizationUsers', (q: any) => callerMembership(ctx, q));

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isChannelOrgMember = (ctx: QueryCtx, eb: any) =>
  eb.or(
    eb.exists('members', (mq: any) =>
      mq.whereExists('user', (uq: any) => uq.where('clerkId', '=', ctx.sub))
    ),
    eb.and(
      eb.cmp('dmChannel', '=', false),
      eb.cmp('isPrivate', '=', false),
      eb.exists('organizations', (oq: any) =>
        oq.whereExists('organizationUsers', (ouq: any) =>
          callerMembership(ctx, ouq)
        )
      )
    )
  );

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isOwnOrSharedOrgUserRow = (ctx: QueryCtx, eb: any) =>
  eb.or(
    eb.exists('user', (uq: any) => uq.where('clerkId', '=', ctx.sub)),
    eb.exists('organizations', (oq: any) =>
      oq.whereExists('organizationUsers', (ouq: any) =>
        callerMembership(ctx, ouq)
      )
    )
  );

/** Org is visible: the shared PUBLIC catalog org, or the caller's own org. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isVisibleOrgRow = (ctx: QueryCtx, oq: any) =>
  oq.where((eb2: any) =>
    eb2.or(
      eb2.cmp('id', '=', PUBLIC_ORGANIZATION_ID),
      eb2.exists('organizationUsers', (ouq: any) => callerMembership(ctx, ouq))
    )
  );

/**
 * Strict version of isVisibleOrgRow with no PUBLIC_ORGANIZATION_ID exemption.
 * Called as `.where(eb => isOwnOrgRow(ctx, eb))`, so `eb` is an
 * ExpressionBuilder (`.exists`/`.cmp`/`.or`), not a relationship query
 * builder -- unlike zero-schema.ts's now-removed declarative version of this
 * predicate, which ran inside a `.whereExists(...)` callback and so took a
 * query builder instead. Confirmed by the exact `oq.whereExists is not a
 * function` TypeError this used to throw when it still called `oq.whereExists`.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isOwnOrgRow = (ctx: QueryCtx, eb: any) =>
  eb.exists('organizationUsers', (ouq: any) => callerMembership(ctx, ouq));

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isOwnedByVisibleOrg = (ctx: QueryCtx, eb: any) =>
  eb.exists('viaOrgEdge', (peq: any) =>
    peq.whereExists('sourceOrganization', (oq: any) => isVisibleOrgRow(ctx, oq))
  );

/** Own row only: a private per-user default search (mirrors zero-schema.ts). */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isOwnPreferenceRow = (ctx: QueryCtx, eb: any) =>
  eb.exists('user', (uq: any) => uq.where('clerkId', '=', ctx.sub));

/**
 * Kept in sync with CAPABILITIES.ManageReference in src/utils/capabilities.ts's
 * ROLE_CAPABILITIES (and with zero-schema.ts's copy of this same list).
 */
const MANAGE_REFERENCE_ROLES = ['admin', 'owner', 'gm', 'mgr'] as const;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const hasManageReference = (ctx: QueryCtx, eb: any) =>
  eb.exists('organization', (oq: any) =>
    oq.whereExists('organizationUsers', (ouq: any) =>
      ouq
        .where('role', 'IN', MANAGE_REFERENCE_ROLES)
        .whereExists('user', (uq: any) => uq.where('clerkId', '=', ctx.sub))
    )
  );

/**
 * Kept in sync with CAPABILITIES.ViewUnavailableItemAnalytics in
 * src/utils/capabilities.ts's ROLE_CAPABILITIES. Called on an
 * `organizations` row: the caller holds an admin membership in that org.
 */
const EIGHTY_SIX_ANALYTICS_ROLES = ['admin'] as const;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isEightySixAnalyticsViewer = (ctx: QueryCtx, eb: any) =>
  eb.exists('organizationUsers', (ouq: any) =>
    ouq
      .where('role', 'IN', EIGHTY_SIX_ANALYTICS_ROLES)
      .whereExists('user', (uq: any) => uq.where('clerkId', '=', ctx.sub))
  );

/**
 * Port of zero-schema.ts's `userContacts` row select rule: the owner always,
 * org peers only when the owner opted in with `shareContactWithOrg`. Apply it
 * to every `related('contact')` and `exists('contact')` in a synced query.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isVisibleUserContactRow = (ctx: QueryCtx, eb: any) =>
  eb.or(
    eb.exists('user', (uq: any) => uq.where('clerkId', '=', ctx.sub)),
    eb.exists('user', (uq: any) =>
      uq
        .where('shareContactWithOrg', '=', true)
        .whereExists('organizationUsers', (ouq: any) =>
          ouq.whereExists('organizations', (oq: any) =>
            oq.whereExists('organizationUsers', (ou2q: any) =>
              callerMembership(ctx, ou2q)
            )
          )
        )
    )
  );

/** Published: any org member. Draft/archived: ManageReference holders only. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isVisibleKnowledgeArticleRow = (ctx: QueryCtx, eb: any) =>
  eb.or(
    eb.and(
      eb.cmp('status', '=', 'published'),
      eb.exists('organization', (oq: any) =>
        oq.whereExists('organizationUsers', (ouq: any) =>
          callerMembership(ctx, ouq)
        )
      )
    ),
    hasManageReference(ctx, eb)
  );

/**
 * Port of zero-schema.ts's `files` row select rule: active membership in the
 * file's organization, plus the parent's own visibility (chat files inherit
 * the channel's public/DM rule; menu and recipe files need the org edge and
 * a readable parent row).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isVisibleFileRow = (ctx: QueryCtx, eb: any) =>
  eb.and(
    eb.exists('organization', (oq: any) =>
      oq.whereExists('organizationUsers', (ouq: any) =>
        callerMembership(ctx, ouq)
      )
    ),
    eb.or(
      eb.and(
        eb.cmp('parentType', '=', 'chat_messages'),
        eb.exists('chatMessage', (mq: any) =>
          mq.whereExists('chatChannels', (cq: any) =>
            cq.where((channelEb: any) => isChannelOrgMember(ctx, channelEb))
          )
        )
      ),
      eb.and(
        eb.cmp('parentType', '=', 'menus'),
        eb.exists('parentOrganizationEdge', (q: any) => q),
        eb.exists('menu', (q: any) => q)
      ),
      eb.and(
        eb.cmp('parentType', '=', 'recipes'),
        eb.exists('parentOrganizationEdge', (q: any) => q),
        eb.exists('recipe', (q: any) => q)
      ),
      eb.and(
        eb.cmp('parentType', '=', 'ingredients'),
        eb.exists('parentOrganizationEdge', (q: any) => q),
        eb.exists('ingredient', (q: any) => q)
      )
    )
  );

/**
 * Port of zero-schema.ts's `menus` row select rule: the menu is linked from
 * an org the caller belongs to (no PUBLIC_ORGANIZATION_ID exemption -- menus
 * are per-org business data).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isVisibleMenuRow = (ctx: QueryCtx, eb: any) =>
  eb.exists('viaOrgEdge', (peq: any) =>
    peq.whereExists('sourceOrganization', (oq: any) =>
      oq.whereExists('organizationUsers', (ouq: any) =>
        callerMembership(ctx, ouq)
      )
    )
  );

/**
 * Port of zero-schema.ts's `menuSections`/`menuItems` row select rule:
 * active membership in the row's organization AND the parent menu is
 * visible (isVisibleMenuRow). Apply it to every `.related('sections' |
 * 'items' | 'menuItems')` and `exists()` that reaches these tables.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isVisibleMenuChildRow = (ctx: QueryCtx, eb: any) =>
  eb.and(
    eb.exists('organization', (oq: any) =>
      oq.whereExists('organizationUsers', (ouq: any) =>
        callerMembership(ctx, ouq)
      )
    ),
    eb.exists('menu', (mq: any) =>
      mq.where((menuEb: any) => isVisibleMenuRow(ctx, menuEb))
    )
  );
